import { useRef, type PointerEvent } from "react";

/** How far the pointer must travel while pressed before a press reads as a drag. */
const DRAG_THRESHOLD_PX = 6;

/**
 * Notice a drag the user may not make, so it can be refused with a message
 * instead of the item simply not moving.
 *
 * Spread `handlers` onto an element whose drag is disabled. A press that
 * travels past the threshold calls `onBlocked` once for that gesture; a plain
 * click never does. `consumeClick()` tells the click that ends such a gesture
 * to do nothing, so refusing a drag does not also open the item.
 */
export function useBlockedDragNotice(onBlocked: () => void, enabled: boolean) {
  const start = useRef<{ x: number; y: number } | null>(null);
  const noticed = useRef(false);

  const handlers = enabled
    ? {
        onPointerDown: (event: PointerEvent) => {
          if (event.button !== 0) return;
          start.current = { x: event.clientX, y: event.clientY };
          noticed.current = false;
        },
        onPointerMove: (event: PointerEvent) => {
          const origin = start.current;
          if (!origin || noticed.current) return;
          const moved = Math.hypot(event.clientX - origin.x, event.clientY - origin.y);
          if (moved < DRAG_THRESHOLD_PX) return;
          noticed.current = true;
          onBlocked();
        },
        onPointerUp: () => {
          start.current = null;
        },
        onPointerCancel: () => {
          start.current = null;
        },
      }
    : {};

  /** True (once) when the click ends a refused drag and should be ignored. */
  const consumeClick = () => {
    if (!noticed.current) return false;
    noticed.current = false;
    return true;
  };

  return { handlers, consumeClick };
}
